import type { Action } from "../actions/types";

/**
 * Outcome of a single load row.
 *
 * `status` is deliberately three-valued rather than a boolean. A row that was
 * skipped because its idempotency key had already been used moved no money,
 * and reporting it as a success would tell the operator a re-uploaded file had
 * loaded everyone a second time.
 */
export interface FundsLoadRowResult {
  rowNumber: number;
  identifier: string;
  product: string;
  amount: number;
  status: "pending" | "loaded" | "skipped" | "failed";
  message: string;
  transactionGuid?: string;
  availableBalance?: number;
}

export interface FundsLoadMember {
  personGuid: string;
  name: string;
  email: string;
}

export interface FundsLoadBusiness {
  businessGuid: string;
  name: string;
}

export interface FundsLoadState {
  submitting: boolean;
  completed: boolean;
  results: FundsLoadRowResult[];
  members: FundsLoadMember[];
  membersLoading: boolean;
  businesses: FundsLoadBusiness[];
  businessesLoading: boolean;
}

const initialState: FundsLoadState = {
  submitting: false,
  completed: false,
  results: [],
  members: [],
  membersLoading: false,
  businesses: [],
  businessesLoading: false,
};

const fundsLoadReducer = (
  state: FundsLoadState = initialState,
  action: Action
): FundsLoadState => {
  switch (action.type) {
    case "SUBMIT_FUNDS_LOAD":
      // The rows are seeded as pending up front so the results table can be
      // rendered in full while the calls are still going out, rather than
      // growing a row at a time.
      return {
        submitting: true,
        completed: false,
        results: (action.data?.rows || []).map(
          (row: any, index: number): FundsLoadRowResult => ({
            rowNumber: index + 1,
            identifier: row.email || row.personGuid || "",
            product: row.product,
            amount: Number(row.amount),
            status: "pending",
            message: "",
          })
        ),
      };
    case "SET_FUNDS_LOAD_ROW_RESULT": {
      const results = state.results.map((result) =>
        result.rowNumber === action.data.rowNumber
          ? { ...result, ...action.data }
          : result
      );
      return { ...state, results };
    }
    case "FETCH_LOADABLE_BUSINESSES":
      return { ...state, businessesLoading: true };
    case "SET_LOADABLE_BUSINESSES":
      return {
        ...state,
        businesses: action.data || [],
        businessesLoading: false,
      };
    case "FETCH_FUNDS_LOAD_MEMBERS":
      // The members of the previous company must not linger while the new
      // ones load, or the picker briefly offers people from another company.
      return { ...state, members: [], membersLoading: true };
    case "SET_FUNDS_LOAD_MEMBERS":
      return { ...state, members: action.data || [], membersLoading: false };
    case "SET_FUNDS_LOAD_COMPLETED":
      return { ...state, submitting: false, completed: true };
    case "CLEAR_FUNDS_LOAD":
      // The company list is expensive to fetch and does not change between
      // loads, so it survives a reset.
      return {
        ...initialState,
        businesses: state.businesses,
        businessesLoading: state.businessesLoading,
      };
    default:
      return state;
  }
};

export default fundsLoadReducer;
